import api from 'api'
import { Button, HFlow, Icon, Text, Tooltip } from 'bold-ui'
import { usePecField } from 'components/form'
import { confirm } from 'components/modals/confirm'
import { ButtonLink } from 'components/route'
import { NavigationButton } from 'components/route/NavigationButton'
import { useFlags } from 'config/useFlagsContext'
import { useRegistrosMChatQuery } from 'graphql/hooks.generated'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useServerTime } from 'hooks/useServerTime'
import { isEmpty } from 'util/checks'
import { MetaPath } from 'util/metaPath'
import { LogEventType } from 'view/atendimentos/atendimento-individual/logFinalizarAtendimento'

import { MChatRfAccordionSection, MChatRfAccordionSectionProps } from '../../components/MChatRfAccordionSection'
import { MChatRfAccordionStepper } from '../../components/MChatRfAccordionStepper'
import { MChatAccessDocLink } from '../../mchat/components/MChatAccessDocLink'
import { MChatRfAccordionSectionEnum } from '../../model-mChatRf'
import { convertConsultaSeguimentoFieldToViewModel } from '../converter-consultaSeguimento'
import {
  ConsultaSeguimentoFieldModel,
  ConsultaSeguimentoViewModel,
  ImpressaoConsultaSeguimento,
} from '../model-consultaSeguimento'
import { getAlertaConsultaSeguimentoAnterior } from '../utils-consultaSeguimento'

interface ConsultaSeguimentoFieldAccordionSectionProps
  extends Omit<MChatRfAccordionSectionProps, 'section' | 'value' | 'stepper' | 'primaryActions' | 'secondaryActions'> {
  name: MetaPath<ConsultaSeguimentoFieldModel>
  valuesAtendimentoAnterior: ConsultaSeguimentoViewModel | null
  mChatAtualRisco: MChatRiscoEnum | undefined
  mChatAnteriorRisco: MChatRiscoEnum | undefined
  isConsultaSeguimentoDesatualizada: boolean
  modalUrl: string
  aplicarButtonRef: React.RefObject<HTMLButtonElement>
  onDelete: () => void
}

export function ConsultaSeguimentoFieldAccordionSection(props: Readonly<ConsultaSeguimentoFieldAccordionSectionProps>) {
  const {
    name,
    valuesAtendimentoAnterior,
    modalUrl,
    mChatAtualRisco,
    mChatAnteriorRisco,
    aplicarButtonRef,
    isConsultaSeguimentoDesatualizada,
    onDelete,
    ...sectionProps
  } = props

  const { PILOTO_M_CHAT_RF_ENABLED } = useFlags()
  const { analytics } = useAnalytics()
  const { getServerTimeNow } = useServerTime()

  const logAvaliarReavaliarMChatRfEvent = () => {
    if (isReavaliar) {
      analytics.logEvent('click_reavaliar_mchatrf')
    } else {
      analytics.logEvent('click_avaliar_mchatrf')
    }
  }

  const {
    atendimentoProfissional: { id: atendProfId } = {},
    cidadao: { id: cidadaoId },
    prontuario: { id: prontuarioId },
  } = useAtendimentoContext()

  const {
    input: { value },
  } = usePecField({ name, subscription: { value: true } })

  const { data: { registrosMChat } = {} } = useRegistrosMChatQuery({ variables: { prontuarioId } })

  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao<ImpressaoConsultaSeguimento>()

  const hasDadosAtendimentoAtual = !isEmpty(value)
  const hasDadosAtendimentoAnterior = !isEmpty(valuesAtendimentoAnterior)
  const hasDados = hasDadosAtendimentoAtual || hasDadosAtendimentoAnterior

  const isMChatAtualRiscoMedio = mChatAtualRisco === MChatRiscoEnum.MEDIO
  const isMChatAnteriorRiscoMedio = mChatAnteriorRisco === MChatRiscoEnum.MEDIO

  const isConsultaSeguimentoAtiva = isMChatAtualRiscoMedio || isMChatAnteriorRiscoMedio || hasDados
  const hideDivider = !hasDados && (isMChatAtualRiscoMedio || isMChatAnteriorRiscoMedio)
  const isReavaliar = hasDadosAtendimentoAnterior && !hasDadosAtendimentoAtual
  const alertaConsultaSeguimentoAnterior = getAlertaConsultaSeguimentoAnterior(
    value?.resultado,
    getServerTimeNow(),
    registrosMChat
  )

  const resolvedValue = hasDadosAtendimentoAtual
    ? convertConsultaSeguimentoFieldToViewModel(value)
    : valuesAtendimentoAnterior

  const showImpressao = hasDados && resolvedValue && !isConsultaSeguimentoDesatualizada

  const handlePrint = (valueToPrint: ConsultaSeguimentoViewModel) => {
    const input: ImpressaoConsultaSeguimento = {
      atendProfId: hasDadosAtendimentoAtual
        ? atendProfId
        : valuesAtendimentoAnterior?.footerInfo?.atendimentoProfissionalId,
      cidadaoId: cidadaoId,
      totalPerguntasRespondidas: valueToPrint.totalPerguntasRespondidas,
      totalPerguntasFalhou: valueToPrint.totalPerguntasFalhou,
      totalPerguntasPassou: valueToPrint.totalPerguntasPassou,
      resultadoSeguimento: valueToPrint.resultado,
      aplicacaoRf: valueToPrint.aplicacao,
    }

    handleImpressaoPath({
      apiPath: api.mchat.imprimirMChat,
      values: input,
      file: `MChat.pdf`,
      dateFormat: reportFormatDate,
    }).catch(handleImpressaoError)
  }

  const handleDelete = () => confirmDeleteDialog(onDelete, analytics.logEvent)

  return (
    <MChatRfAccordionSection
      section={MChatRfAccordionSectionEnum.CONSULTA_SEGUIMENTO}
      value={resolvedValue}
      isConsultaSeguimentoAtiva={isConsultaSeguimentoAtiva}
      isConsultaSeguimentoDesatualizada={isConsultaSeguimentoDesatualizada}
      hideDivider={hideDivider}
      stepper={
        <MChatRfAccordionStepper
          showPendingIcon={!isConsultaSeguimentoDesatualizada && !hideDivider && !isConsultaSeguimentoAtiva}
          showCheckIcon={!isConsultaSeguimentoDesatualizada && hasDados}
          showConnector={false}
          showInfoIcon={hideDivider}
          showErrorIcon={isConsultaSeguimentoDesatualizada}
        />
      }
      primaryActions={
        !PILOTO_M_CHAT_RF_ENABLED ? (
          <MChatAccessDocLink />
        ) : (
          !hasDadosAtendimentoAtual && (
            <NavigationButton
              kind={isConsultaSeguimentoAtiva && !isReavaliar ? 'primary' : 'normal'}
              size='small'
              to={modalUrl}
              innerRef={aplicarButtonRef}
              onClick={logAvaliarReavaliarMChatRfEvent}
            >
              {isReavaliar ? 'Reavaliar' : 'Avaliar'}
            </NavigationButton>
          )
        )
      }
      secondaryActions={
        <HFlow hSpacing={0.5}>
          {showImpressao && (
            <Tooltip text='Imprimir'>
              <Button type='button' kind='normal' skin='ghost' size='small' onClick={() => handlePrint(resolvedValue)}>
                <Icon icon='printerOutline' />
              </Button>
            </Tooltip>
          )}
          {hasDadosAtendimentoAtual && (
            <HFlow hSpacing={0.5}>
              <Tooltip text='Editar'>
                <ButtonLink
                  type='button'
                  kind='normal'
                  skin='ghost'
                  size='small'
                  disabled={!hasDadosAtendimentoAtual}
                  to={modalUrl}
                  onClick={() => {
                    analytics.logEvent('click_editar_mchatrf')
                  }}
                >
                  <Icon icon='penOutline' />
                </ButtonLink>
              </Tooltip>
              <Tooltip text='Excluir'>
                <Button type='button' kind='normal' skin='ghost' size='small' onClick={handleDelete}>
                  <Icon icon='trashOutline' />
                </Button>
              </Tooltip>
            </HFlow>
          )}
        </HFlow>
      }
      isRegistradoAgora={hasDadosAtendimentoAtual}
      alertaConsultaSeguimentoAnterior={alertaConsultaSeguimentoAnterior}
      {...sectionProps}
    />
  )
}

const confirmDeleteDialog = (handleConfirm: () => void, analyticsLogEvent: LogEventType) => {
  confirm({
    title: 'Deseja excluir a avaliação?',
    icon: 'exclamationTriangleOutline',
    confirmLabel: 'Excluir',
    cancelLabel: 'Cancelar',
    type: 'danger',
    size: 'auto',
    onConfirm: () => {
      analyticsLogEvent('click_excluir_mchatrf')
      handleConfirm()
    },
    render: () => <Text>As informações registradas e o resultado da Entrevista de Seguimento serão perdidos.</Text>,
  })()
}
